import { ActionKeys } from '~/actions/constants';
import { createBatchTransactionsError } from '~/actions/orders-batch.actions';
import * as storeActions from '~/actions/stores.actions';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { countryHasMasterAccount } from '~/pages/Onboarding/onboarding.constants';
import * as onboardingActions from '~/pages/Onboarding/store/onboarding.actions';
import { call, takeLatest } from 'redux-saga/effects';
import { getDefaultSenderAddress } from '~/selectors/address.selectors';
import { getUserCarrierAccounts } from '~/selectors/carrier-accounts.selectors';
import { getUserId } from '~/selectors/user.selectors';
import { SavedAPIAddress } from '~/types/APIAddress';
import CarrierAccount from '~/types/CarrierAccount';
import { Events, getLocalTraits, identify, isTraitDifferent, track } from '~/utils/segment';
import { selectState } from '..';
import { trackWithGlobalProps } from '~/utils/TrackingHelper/utils';

export function* userCarrierAccountTraits() {
  const userId: number | undefined | null = yield selectState<number | undefined | null>(getUserId);
  const userCarrierAccounts: readonly CarrierAccount[] =
    yield selectState<readonly CarrierAccount[]>(getUserCarrierAccounts);
  if (userCarrierAccounts.length) {
    const localTraits = getLocalTraits();
    const personalCarrierNames = uniq(userCarrierAccounts.map((carrier) => carrier.carrier.name));
    if (
      (localTraits && !localTraits.personal_carriers) ||
      (localTraits &&
        localTraits.personal_carriers &&
        Array.isArray(localTraits.personal_carriers) &&
        !isEqual(personalCarrierNames, localTraits.personal_carriers))
    ) {
      yield call(identify, userId || undefined, { personal_carriers: personalCarrierNames });
    }
  }
}

export function* userAddressTraits() {
  const defaultSenderAddress: SavedAPIAddress | null = yield selectState<SavedAPIAddress | null>(
    getDefaultSenderAddress,
  );

  // NOTE(losch#ENG-21086|2022-01-14): Fix for legacy accounts whom didn't go through the onboarding flow.
  if (!defaultSenderAddress) {
    return;
  }

  const userId: number | undefined | null = yield selectState<number | undefined | null>(getUserId);
  if (
    defaultSenderAddress.country &&
    (isTraitDifferent('sender_address_city', defaultSenderAddress.city) ||
      isTraitDifferent('sender_address_phone', defaultSenderAddress.phone) ||
      isTraitDifferent('sender_address_state', defaultSenderAddress.state) ||
      isTraitDifferent('sender_address_country_name', defaultSenderAddress.country?.name) ||
      isTraitDifferent('email', defaultSenderAddress.email))
  ) {
    yield call(identify, userId || undefined, {
      sender_address_company: defaultSenderAddress.company,
      sender_address_city: defaultSenderAddress.city,
      sender_address_state: defaultSenderAddress.state,
      sender_address_country_name: defaultSenderAddress.country?.name,
      sender_address_name: defaultSenderAddress.name,
      sender_address_phone: defaultSenderAddress.phone,
      sender_address_email: defaultSenderAddress.email,
      master_account_available: countryHasMasterAccount(defaultSenderAddress.country),
    });
  }
}

export function* updateCountryHasMasterAccountOnboarding(
  action: ReturnType<typeof onboardingActions.updateOnboardingInfoSuccess>,
) {
  const userId: number | undefined | null = yield selectState<number | undefined | null>(getUserId);
  // Update the master account available user trait on Segment
  yield call(identify, userId || undefined, {
    master_account_available: action.payload.values.country
      ? countryHasMasterAccount(action.payload.values.country)
      : undefined,
  });
}

export function* ordersDownloadLabel() {
  yield call(trackWithGlobalProps, 'other', Events.label_downloaded);
}

export function* ordersDownloadPackingSlip() {
  yield call(track, Events.packing_slip_downloaded);
}

export function* sendLabelPurchaseFailedEvent(
  action: ReturnType<typeof createBatchTransactionsError>,
) {
  yield call(track, Events.label_purchase_failed, { error: action.payload.error });
}

export function* sendStoreDisconnectedEvent(
  action: ReturnType<typeof storeActions.disconnectStoreSuccess>,
) {
  yield call(track, Events.store_disconnected, {
    store: action.storeName,
  });
}

export default [
  takeLatest(
    [ActionKeys.ORDERS_GET_ALL_LABELS_SUCCESS, ActionKeys.ORDERS_GET_LABEL_SUCCESS],
    ordersDownloadLabel,
  ),
  takeLatest(ActionKeys.ORDERS_GET_PACKING_SLIP, ordersDownloadPackingSlip),
  takeLatest(
    [ActionKeys.FETCH_CARRIER_ACCOUNTS_SUCCESS, ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS],
    userCarrierAccountTraits,
  ),
  takeLatest(
    [
      ActionKeys.ADDRESSES_FETCH_SUCCESS,
      ActionKeys.APIADDRESS_CREATE,
      ActionKeys.APIADDRESS_EDIT,
      ActionKeys.APIADDRESS_DELETE,
    ],
    userAddressTraits,
  ),
  takeLatest(
    [onboardingActions.OnboardingActionKeys.UPDATE_ONBOARDING_INFO_SUCCESS],
    updateCountryHasMasterAccountOnboarding,
  ),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_ERROR, sendLabelPurchaseFailedEvent),
  takeLatest(ActionKeys.DISCONNECT_STORE_SUCCESS, sendStoreDisconnectedEvent),
];
